import React, { useCallback, useState } from 'react';
import {
  Box,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Close as CloseIcon, Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import type { MapBookmark } from '../types/webMap';

export interface MapBookmarksPanelProps {
  bookmarks: MapBookmark[];
  onGoTo: (bm: MapBookmark) => void;
  onAdd: (bm: MapBookmark) => void;
  onDelete: (id: string) => void;
  onClose: () => void;
  currentCenter: [number, number];
  currentZoom: number;
  currentBearing?: number;
  currentPitch?: number;
}

const MapBookmarksPanel: React.FC<MapBookmarksPanelProps> = ({
  bookmarks,
  onGoTo,
  onAdd,
  onDelete,
  onClose,
  currentCenter,
  currentZoom,
  currentBearing,
  currentPitch,
}) => {
  const [newName, setNewName] = useState('');

  const handleAdd = useCallback(() => {
    const name = newName.trim();
    if (!name) return;
    onAdd({
      id: `bm_${Date.now()}`,
      name,
      center: currentCenter,
      zoom: currentZoom,
      bearing: currentBearing,
      pitch: currentPitch,
    });
    setNewName('');
  }, [newName, currentCenter, currentZoom, currentBearing, currentPitch, onAdd]);

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 60,
        left: 54,
        zIndex: 10,
        bgcolor: 'rgba(30,30,30,0.94)',
        backdropFilter: 'blur(6px)',
        borderRadius: 1,
        p: 1.5,
        width: 240,
        maxHeight: 360,
        overflow: 'auto',
        boxShadow: 3,
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="caption" fontWeight={600} color="text.primary">
          Bookmarks
        </Typography>
        <IconButton size="small" onClick={onClose} sx={{ p: 0.25 }}>
          <CloseIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Stack>

      <Stack direction="row" spacing={0.5} sx={{ mb: 1 }}>
        <TextField
          size="small"
          placeholder="Bookmark name"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
          sx={{ flex: 1, '& .MuiInputBase-input': { fontSize: '0.75rem', py: 0.75 } }}
        />
        <IconButton size="small" onClick={handleAdd} disabled={!newName.trim()} color="primary">
          <AddIcon fontSize="small" />
        </IconButton>
      </Stack>

      {bookmarks.length === 0 ? (
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', textAlign: 'center', py: 2 }}>
          No bookmarks yet. Save the current view above.
        </Typography>
      ) : (
        <List dense disablePadding>
          {bookmarks.map((bm) => (
            <ListItemButton key={bm.id} onClick={() => onGoTo(bm)} sx={{ borderRadius: 0.5, py: 0.25 }}>
              <ListItemText
                primary={bm.name}
                secondary={`${bm.center[0].toFixed(3)}, ${bm.center[1].toFixed(3)} z${bm.zoom.toFixed(1)}`}
                primaryTypographyProps={{ variant: 'caption', fontWeight: 500 }}
                secondaryTypographyProps={{ variant: 'caption', fontSize: '0.65rem' }}
              />
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); onDelete(bm.id); }}
                sx={{ p: 0.25, opacity: 0.5, '&:hover': { opacity: 1 } }}
              >
                <DeleteIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </ListItemButton>
          ))}
        </List>
      )}
    </Box>
  );
};

export default MapBookmarksPanel;
